import { Injectable, inject, signal, OnDestroy } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { Subscription } from 'rxjs';
import { AuthService } from '../../../Services/auth.service';
import { LocalStorageService } from '../../../Services/local-storage.service';
import { User } from '../../../Models/user.model';
import { Language } from '../Types/language.type';
import { LanguageOption } from '../Interfaces/language-option.interface';

@Injectable({
  providedIn: 'root',
})
export class TranslationService implements OnDestroy {
  private translateService = inject(TranslateService);
  private authService = inject(AuthService);
  private localStorageService = inject(LocalStorageService);
  private userSubscription?: Subscription;
  private readonly LANGUAGE_KEY = 'preferred_lang';

  public currentLanguage = signal<Language>('en');
  public readonly availableLanguages: LanguageOption[] = [
    { code: 'en', label: 'English' },
    { code: 'fr', label: 'Français' },
  ];

  constructor() {
    this.initLanguage();
    this.subscribeToUserChanges();
  }

  ngOnDestroy(): void {
    this.userSubscription?.unsubscribe();
  }

  private initLanguage(): void {
    this.authService.currentUser$
      .subscribe((user) => {
        const userLang = this.extractUserLanguage(user);
        if (userLang && this.isValidLanguage(userLang)) {
          this.setLanguage(userLang);
          return;
        }

        this.setLanguage(this.resolveColdLoadLanguage());
      })
      .unsubscribe();
  }

  // Pre-auth language
  resolveColdLoadLanguage(): Language {
    const storedLang = this.localStorageService.getItem(this.LANGUAGE_KEY) as Language | null;
    if (storedLang && this.isValidLanguage(storedLang)) {
      return storedLang;
    }

    const browserLang = this.translateService.getBrowserLang();
    return browserLang?.startsWith('fr') ? 'fr' : 'en';
  }

  private subscribeToUserChanges(): void {
    this.userSubscription = this.authService.currentUser$.subscribe((user) => {
      const userLang = this.extractUserLanguage(user);
      if (userLang && this.isValidLanguage(userLang) && userLang !== this.currentLanguage()) {
        this.setLanguage(userLang);
      }
    });
  }

  setLanguage(lang: Language): void {
    this.translateService.use(lang).subscribe({
      next: () => {
        this.currentLanguage.set(lang);
        this.localStorageService.setItem(this.LANGUAGE_KEY, lang);
      },
    });
  }

  getLanguage(): Language {
    return this.currentLanguage();
  }

  instant(key: string | string[], interpolateParams?: object): string {
    return this.translateService.instant(key, interpolateParams);
  }

  private extractUserLanguage(user: User | null): string | null {
    if (!user) {
      return null;
    }

    const record = user as unknown as Record<string, unknown>;
    const value = record['preferred_lang'] ?? record['preferredLanguage'];
    return typeof value === 'string' ? value : null;
  }

  private isValidLanguage(lang: string): lang is Language {
    return lang === 'en' || lang === 'fr';
  }
}
